import React from 'react';
import { Input, Select, Toggle } from '../../components/ui';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';

interface PartDef {
  name: string;
  displayName: string;
  icon: string;
  fields?: PartField[];
}

interface PartField {
  key: string;
  label: string;
  type: 'number' | 'select';
  default: unknown;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
}

const PARTS: PartDef[] = [
  {
    name: 'summary',
    displayName: 'Summary',
    icon: '\u{1F4DD}',
    fields: [{ key: 'maxLength', label: 'Max length', type: 'number', default: 500, min: 50, max: 5000, step: 50 }],
  },
  {
    name: 'table',
    displayName: 'Table',
    icon: '\u{1F4CA}',
    fields: [{ key: 'maxRows', label: 'Max rows', type: 'number', default: 100, min: 1, max: 10000 }],
  },
  {
    name: 'chart',
    displayName: 'Chart',
    icon: '\u{1F4C8}',
    fields: [
      {
        key: 'chartType',
        label: 'Chart type',
        type: 'select',
        default: 'auto',
        options: [
          { value: 'auto', label: 'Auto' },
          { value: 'line', label: 'Line' },
          { value: 'bar', label: 'Bar' },
          { value: 'pie', label: 'Pie' },
        ],
      },
    ],
  },
  {
    name: 'signal',
    displayName: 'Signal',
    icon: '\u{1F4E1}',
    fields: [
      { key: 'minConfidence', label: 'Min confidence', type: 'number', default: 0.5, min: 0, max: 1, step: 0.05 },
    ],
  },
  { name: 'metric', displayName: 'Metric', icon: '\u{1F4CF}' },
  {
    name: 'file',
    displayName: 'File',
    icon: '\u{1F4C1}',
    fields: [
      {
        key: 'format',
        label: 'Format',
        type: 'select',
        default: 'json',
        options: [
          { value: 'json', label: 'JSON' },
          { value: 'csv', label: 'CSV' },
          { value: 'markdown', label: 'Markdown' },
        ],
      },
    ],
  },
  {
    name: 'report',
    displayName: 'Report',
    icon: '\u{1F4CB}',
    fields: [{ key: 'maxSections', label: 'Max sections', type: 'number', default: 10, min: 1, max: 50 }],
  },
  { name: 'schema', displayName: 'Schema', icon: '\u{1F9EC}' },
  { name: 'generated_code', displayName: 'Generated Code', icon: '\u{1F4BB}' },
];

const VISIBILITY_OPTIONS = [
  { value: 'run', label: 'Run only' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'feed', label: 'Feed' },
];

interface StepOutputTabProps {
  step: WorkflowBuilderStep;
  onUpdateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
}

export const StepOutputTab: React.FC<StepOutputTabProps> = ({ step, onUpdateStep }) => {
  const outputConfig = (step.outputConfig ?? { parts: {}, processors: [], auto: false }) as {
    parts?: Record<string, unknown>;
    processors?: Array<Record<string, unknown>>;
    auto?: boolean;
  };
  const parts = outputConfig.parts ?? {};

  const update = (newConfig: typeof outputConfig) => {
    onUpdateStep(step.id.toString(), { outputConfig: newConfig });
  };

  const isEnabled = (name: string) => {
    const val = parts[name];
    return val !== undefined && val !== false;
  };

  const getPartConfig = (name: string): Record<string, unknown> => {
    const val = parts[name];
    if (!val || val === true) return {};
    return val as Record<string, unknown>;
  };

  const togglePart = (name: string) => {
    const newParts = { ...parts };
    if (isEnabled(name)) {
      newParts[name] = false;
    } else {
      newParts[name] = { visibility: name === 'metric' ? 'dashboard' : name === 'signal' ? 'feed' : 'run' };
    }
    update({ ...outputConfig, parts: newParts });
  };

  const updatePartField = (name: string, field: string, value: unknown) => {
    const cfg = { ...getPartConfig(name), [field]: value };
    update({ ...outputConfig, parts: { ...parts, [name]: cfg } });
  };

  return (
    <div className="space-y-4">
      {/* Auto-extract toggle */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Toggle
          checked={outputConfig.auto ?? false}
          onChange={(auto) => update({ ...outputConfig, auto })}
          label="Auto-extract"
          size="sm"
        />
        <span className="text-muted">— automatically extract standard output parts when possible</span>
      </div>

      <div className="border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[1fr_60px_120px_1fr] gap-2 px-3 py-2 bg-surface-alt text-xs font-medium text-muted border-b">
          <span>Output part</span>
          <span className="text-center">On</span>
          <span>Visibility</span>
          <span>Config</span>
        </div>

        {/* Rows */}
        {PARTS.map((part) => {
          const enabled = isEnabled(part.name);
          const cfg = getPartConfig(part.name);

          return (
            <div
              key={part.name}
              className={`grid grid-cols-[1fr_60px_120px_1fr] gap-2 px-3 py-2 items-center border-b last:border-b-0 ${
                enabled ? 'bg-surface' : 'bg-surface opacity-60'
              }`}
            >
              {/* Name */}
              <span className="text-sm text-body flex items-center gap-1.5">
                <span>{part.icon}</span>
                <span>{part.displayName}</span>
              </span>

              {/* Toggle */}
              <div className="flex justify-center">
                <Toggle
                  checked={enabled}
                  onChange={() => togglePart(part.name)}
                  aria-label={`${part.displayName} output part`}
                  size="sm"
                />
              </div>

              {/* Visibility */}
              {enabled ? (
                <Select
                  value={(cfg.visibility as string) ?? 'run'}
                  onChange={(e) => updatePartField(part.name, 'visibility', e.target.value)}
                  aria-label={`${part.displayName} visibility`}
                >
                  {VISIBILITY_OPTIONS.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="text-xs text-faint">—</span>
              )}

              {/* Config fields */}
              {enabled && part.fields ? (
                <div className="flex items-center gap-2">
                  {part.fields.map((f) => (
                    <label key={f.key} className="flex items-center gap-1 text-xs text-muted">
                      <span>{f.label}:</span>
                      {f.type === 'number' ? (
                        <Input
                          type="number"
                          value={(cfg[f.key] as number) ?? f.default}
                          min={f.min}
                          max={f.max}
                          step={f.step ?? 1}
                          onChange={(e) => updatePartField(part.name, f.key, Number(e.target.value))}
                          className="w-20"
                        />
                      ) : (
                        <Select
                          value={(cfg[f.key] as string) ?? f.default}
                          onChange={(e) => updatePartField(part.name, f.key, e.target.value)}
                        >
                          {f.options!.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-faint">{enabled ? '—' : ''}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
